import { StateCreator } from 'zustand';
import { api } from '../../api/client';

export interface Deal {
  id: string;
  _id?: string;
  title: string;
  client_id?: string;
  lead_id?: string;
  product_id?: string;
  quantity?: number;
  amount: number;
  status: string;
  pipeline_id?: string;
  stage_id?: string;
  probability?: number;
  expected_close_date?: string;
  created_at?: string;
  updated_at?: string;
  _optimistic?: boolean;
}

export interface DealsPagination {
  page: number;
  totalPages: number;
  total: number;
  perPage: number;
}

export interface DealsFilters {
  search?: string;
  status?: string;
  client_id?: string;
  product_id?: string;
}

export interface DealsSlice {
  deals: Deal[];
  dealsLoading: boolean;
  dealsError: string | null;
  dealsPagination: DealsPagination;
  dealsFilters: DealsFilters;
  optimisticDeals: Map<string, Deal>;

  fetchDeals: (filters?: DealsFilters) => Promise<void>;
  createDeal: (data: Partial<Deal>) => Promise<Deal>;
  updateDeal: (id: string, data: Partial<Deal>) => Promise<void>;
  deleteDeal: (id: string) => Promise<void>;

  handleDealSSE: (event: any) => void;

  setDealsFilters: (filters: DealsFilters) => void;
  setDealsPage: (page: number) => void;

  optimisticCreateDeal: (tempId: string, data: Partial<Deal>) => void;
  optimisticUpdateDeal: (id: string, data: Partial<Deal>) => void;
  optimisticDeleteDeal: (id: string) => void;
}

export const createDealsSlice: StateCreator<DealsSlice> = (set, get) => ({
  deals: [],
  dealsLoading: false,
  dealsError: null,
  dealsPagination: {
    page: 1,
    totalPages: 1,
    total: 0,
    perPage: 20,
  },
  dealsFilters: {
    search: '',
  },
  optimisticDeals: new Map(),

  fetchDeals: async (filters?: DealsFilters) => {
    const currentFilters = filters || get().dealsFilters;

    set({ dealsLoading: true, dealsError: null });

    try {
      const params: any = {
        page: get().dealsPagination.page,
        per_page: get().dealsPagination.perPage,
        ...currentFilters,
      };

      const response = await api.get('/deals', { params });

      // API client подменяет response.data на массив и сохраняет пагинацию в response.pagination
      const deals = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      const pagination = response.pagination || response.data || {};

      console.log('[Deals] Response:', response);
      console.log('[Deals] Pagination:', pagination);
      console.log('[Deals] Total pages:', pagination.pages || pagination.total_pages || 1);

      set({
        deals,
        dealsPagination: {
          page: pagination.page || 1,
          totalPages: pagination.pages || pagination.total_pages || 1,
          total: pagination.total || 0,
          perPage: pagination.per_page || 20,
        },
        dealsLoading: false,
      });
    } catch (error: any) {
      set({
        dealsError: error.response?.data?.message || 'Ошибка загрузки сделок',
        dealsLoading: false,
      });
    }
  },

  createDeal: async (data: Partial<Deal>) => {
    try {
      const response = await api.post('/deals', data);
      const newDeal = response.data;

      // Сразу перезагружаем список после создания
      await get().fetchDeals();

      return newDeal;
    } catch (error: any) {
      throw error;
    }
  },

  updateDeal: async (id: string, data: Partial<Deal>) => {
    try {
      const response = await api.put(`/deals/${id}`, data);
      const updated = response.data;

      // Перезагружаем список после обновления
      await get().fetchDeals();

      return updated;
    } catch (error: any) {
      throw error;
    }
  },

  deleteDeal: async (id: string) => {
    try {
      await api.delete(`/deals/${id}`);

      // Перезагружаем список после удаления
      await get().fetchDeals();
    } catch (error: any) {
      throw error;
    }
  },

  handleDealSSE: (event) => {
    const { type, data } = event;

    switch (type) {
      case 'deal.created':
        set((state) => {
          const dealData = data.deal || data.data?.deal || data;
          const dealId = dealData.id || dealData._id;

          // Проверяем есть ли уже такая сделка (по id или _id)
          const exists = state.deals.some((d) => {
            const existingId = d.id || d._id;
            return existingId && (existingId === dealId || existingId === dealData.id || existingId === dealData._id);
          });

          if (!exists && dealId) {
            console.log('[SSE] Adding new deal:', dealId);
            return { deals: [dealData, ...state.deals] };
          }

          console.log('[SSE] Deal already exists, skipping:', dealId);
          return state;
        });
        break;

      case 'deal.updated':
      case 'deal.moved':
        set((state) => {
          const dealData = data.deal || data.data?.deal || data;
          const dealId = data.dealId || dealData.id || dealData._id;

          return {
            deals: state.deals.map((d) => {
              const existingId = d.id || d._id;
              return existingId === dealId ? { ...d, ...dealData, id: dealId } : d;
            }),
          };
        });
        break;

      case 'deal.deleted':
        set((state) => {
          const dealId = data.id || data.data?.id || data.dealId;
          return {
            deals: state.deals.filter((d) => {
              const existingId = d.id || d._id;
              return existingId !== dealId;
            }),
          };
        });
        break;
    }
  },

  setDealsFilters: (filters: DealsFilters) => {
    set({
      dealsFilters: filters,
      dealsPagination: { ...get().dealsPagination, page: 1 }, // ✅ Сброс на первую страницу
    });
    get().fetchDeals(filters);
  },

  setDealsPage: (page: number) => {
    set((state) => ({
      dealsPagination: { ...state.dealsPagination, page },
    }));
    get().fetchDeals();
  },

  optimisticCreateDeal: (tempId: string, data: Partial<Deal>) => {
    const tempDeal: Deal = {
      id: tempId,
      title: data.title || '',
      amount: data.amount || 0,
      status: data.status || 'new',
      _optimistic: true,
    };

    set((state) => ({
      deals: [tempDeal, ...state.deals],
      optimisticDeals: new Map([...state.optimisticDeals, [tempId, tempDeal]]),
    }));
  },

  optimisticUpdateDeal: (id: string, data: Partial<Deal>) => {
    set((state) => {
      const updated = state.deals.map((d) => {
        if (d.id === id || d._id === id) {
          return { ...d, ...data, _optimistic: true };
        }
        return d;
      });

      const deal = updated.find((d) => d.id === id || d._id === id);

      return {
        deals: updated,
        optimisticDeals: deal
          ? new Map([...state.optimisticDeals, [id, deal]])
          : state.optimisticDeals,
      };
    });
  },

  optimisticDeleteDeal: (id: string) => {
    set((state) => ({
      deals: state.deals.filter((d) => d.id !== id && d._id !== id),
      optimisticDeals: new Map([...state.optimisticDeals, [id, null as any]]),
    }));
  },
});
